import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { submissionApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatDate, formatFileSize, getRiskColor, getStatusColor } from '../lib/utils';
import { 
  FileText, 
  Search, 
  Filter, 
  ArrowRight, 
  Loader2,
  Upload,
  Download
} from 'lucide-react';

const History = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const response = await submissionApi.list(0, 100);
        setSubmissions(response.data);
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = submission.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         submission.file_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    const matchesRisk = riskFilter === 'all' || submission.risk_level === riskFilter;
    
    return matchesSearch && matchesStatus && matchesRisk;
  });

  const handleDownloadReport = async (e, id, format = 'pdf') => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${apiUrl}/api/submissions/${id}/report?format=${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `RBA_Similarity_Report_${id.slice(0, 8)}.${format}`;
      link.target = '_blank';
      
      document.body.appendChild(link);
      
      setTimeout(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }, 0);
      
    } catch (error) {
      console.error('Failed to download report:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="history-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-foreground">Submission History</h1>
            <p className="text-muted-foreground mt-1">
              View and manage all your past document submissions
            </p>
          </div>
          <Link to="/upload">
            <Button data-testid="upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              New Upload
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title or filename..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="risk-filter">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Risk Levels</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        <Card>
          <CardHeader className="border-b border-border/40">
            <CardTitle className="font-serif text-xl">
              {filteredSubmissions.length} Submission{filteredSubmissions.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-12 px-4">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-semibold text-foreground mb-2">
                  {submissions.length === 0 ? 'No submissions yet' : 'No matching submissions'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {submissions.length === 0 
                    ? 'Upload your first document to get started'
                    : 'Try adjusting your search or filters'
                  }
                </p>
                {submissions.length === 0 && (
                  <Link to="/upload">
                    <Button data-testid="empty-upload-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredSubmissions.map((submission) => (
                  <Link
                    key={submission.id}
                    to={`/submission/${submission.id}`}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                    data-testid={`history-row-${submission.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-foreground truncate">{submission.title}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="truncate">{submission.file_name}</span>
                          <span>•</span>
                          <span>{formatFileSize(submission.file_size)}</span>
                          {submission.course && (
                            <>
                              <span>•</span>
                              <span className="truncate">{submission.course}</span>
                            </>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(submission.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {submission.status === 'completed' && submission.similarity_score !== null && (
                        <div className="text-right hidden sm:block">
                          <p className="font-mono text-xl font-bold text-foreground">
                            {submission.similarity_score?.toFixed(1)}%
                          </p>
                          <Badge className={getRiskColor(submission.risk_level)} variant="secondary">
                            {submission.risk_level || 'N/A'}
                          </Badge>
                        </div>
                      )}
                      
                      <Badge className={getStatusColor(submission.status)} variant="secondary">
                        {submission.status === 'processing' && (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        {submission.status}
                      </Badge>

                      {submission.status === 'completed' && (
                        <div className="flex items-center gap-2 hidden md:flex">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDownloadReport(e, submission.id)}
                            data-testid={`download-btn-${submission.id}`}
                            title="Download Report"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default History;
